import React, { useCallback, useEffect, useState } from "react";
import { FiAlertOctagon, FiCheck, FiClock } from "react-icons/fi";
import { macroDataClient, MacroStructureAnomaly } from "./macroDataClient";

/**
 * État DURABLE de la base macroéconomique, en tête de l'écran.
 *
 * <p>La différence avec un avertissement d'import est toute la raison d'être de ce composant : un
 * rapport ne vaut que pour le fichier qu'on vient de déposer, et disparaît au suivant. Ici, tant
 * que la cause n'a pas disparu, la ligne reste : un second classeur au même défaut ne referme
 * rien, il ne fait qu'actualiser la date de dernière observation. La date de première détection,
 * elle, ne bouge jamais : c'est elle qui rend l'ancienneté visible.</p>
 *
 * <p>Chaque ligne porte, quand elle existe, l'action qui la ferme réellement. Sans cette sortie,
 * un bandeau rouge deviendrait permanent, et un bandeau permanent finit toujours par ne plus être
 * lu.</p>
 */

const NATURES: Record<string, { libelle: string; aide: string }> = {
	"sheet-renamed": {
		libelle: "Onglet renommé",
		aide: "Le classeur nomme cet onglet autrement que le catalogue. Adoptez le nom si le changement est définitif.",
	},
	"sheet-unknown": {
		libelle: "Onglet inconnu",
		aide: "Aucun sujet ne réclame cet onglet : ses données ne sont pas publiées.",
	},
	"subject-missing": {
		libelle: "Sujet absent",
		aide: "Ce sujet n'a plus d'onglet dans le classeur : il ne reçoit plus de données.",
	},
	"series-unconfigured": {
		libelle: "Configuration à compléter",
		aide: "L'indicateur est publié, mais il lui manque de quoi être lisible et traduit.",
	},
	"column-unknown": {
		libelle: "Colonne inconnue",
		aide: "Le catalogue ne réclame pas cette colonne : sa donnée est mise de côté, jamais publiée, et le reste du classeur se publie normalement.",
	},
};

function joursDepuis(iso: string): number {
	const detected = new Date(iso).getTime();
	if (Number.isNaN(detected)) return 0;
	return Math.max(0, Math.floor((Date.now() - detected) / 86_400_000));
}

function dateCourte(iso: string): string {
	const value = new Date(iso);
	return Number.isNaN(value.getTime()) ? "-" : value.toLocaleDateString("fr-FR");
}

/** Extrait le nom d'onglet cité entre guillemets français dans le détail d'une anomalie. */
export function extraireNomOnglet(detail: string): string | null {
	const match = /«\s*([^»]+?)\s*»/.exec(detail ?? "");
	return match ? match[1] : null;
}

export const AnomalyBanner: React.FC<{ canWrite: boolean; refreshKey?: number }> = ({ canWrite, refreshKey }) => {
	const [anomalies, setAnomalies] = useState<MacroStructureAnomaly[]>([]);
	const [busy, setBusy] = useState<number | null>(null);

	const charger = useCallback(async () => {
		try {
			setAnomalies(await macroDataClient.anomalies());
		} catch {
			// L'écran macro doit rester utilisable même si ce complément d'information échoue.
			setAnomalies([]);
		}
	}, []);

	useEffect(() => {
		void charger();
	}, [charger, refreshKey]);

	const adopter = async (anomaly: MacroStructureAnomaly) => {
		const onglet = extraireNomOnglet(anomaly.detail);
		// Le sujet est adopté par son identifiant technique, jamais par son libellé : deux sujets
		// peuvent porter des noms voisins, leurs identifiants sont uniques.
		if (!onglet || !anomaly.datasetId) return;
		setBusy(anomaly.id);
		try {
			await macroDataClient.adoptSheetName(onglet, anomaly.datasetId);
			await charger();
		} finally {
			setBusy(null);
		}
	};

	if (anomalies.length === 0) return null;

	return (
		<div data-testid="macro-anomaly-banner" className="mb-6 rounded-2xl border-2 border-red-300 bg-red-50 p-5">
			<div className="flex items-start gap-3">
				<div className="rounded-xl bg-red-100 p-2 text-red-700"><FiAlertOctagon className="h-5 w-5" /></div>
				<div>
					<h2 className="text-base font-bold text-red-900">
						Le format du classeur a changé ({anomalies.length})
					</h2>
					<p className="mt-0.5 text-sm text-red-800">
						Ces écarts restent signalés tant qu'ils n'ont pas été réellement corrigés. Téléverser un
						nouveau classeur au même format ne les fera pas disparaître.
					</p>
				</div>
			</div>
			<div className="mt-4 overflow-x-auto rounded-xl bg-white">
				<table className="min-w-full text-sm">
					<thead>
						<tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
							<th className="px-3 py-2 font-semibold">Nature</th>
							<th className="px-3 py-2 font-semibold">Sujet</th>
							<th className="px-3 py-2 font-semibold">Détail</th>
							<th className="px-3 py-2 font-semibold">Signalé depuis</th>
							<th className="px-3 py-2 font-semibold">Correction</th>
						</tr>
					</thead>
					<tbody>
						{anomalies.map(anomaly => {
							const nature = NATURES[anomaly.kind] ?? { libelle: anomaly.kind, aide: "" };
							const jours = joursDepuis(anomaly.detectedAtUtc);
							const onglet = extraireNomOnglet(anomaly.detail);
							return (
								<tr key={anomaly.id} className="border-b border-slate-100 align-top">
									<td className="px-3 py-2">
										<div className="font-semibold text-slate-800">{nature.libelle}</div>
										<div className="mt-0.5 text-xs text-slate-500">{nature.aide}</div>
									</td>
									<td className="px-3 py-2 font-medium text-slate-700">{anomaly.subject}</td>
									<td className="px-3 py-2 text-slate-700">{anomaly.detail}</td>
									<td className="px-3 py-2 whitespace-nowrap text-slate-600">
										<span className="flex items-center gap-1.5">
											<FiClock className="h-3.5 w-3.5 shrink-0 text-slate-400" />
											{dateCourte(anomaly.detectedAtUtc)}
											{jours > 0 ? <span className="text-xs text-slate-400">({jours} j)</span> : null}
										</span>
									</td>
									<td className="px-3 py-2">
										{anomaly.kind === "sheet-renamed" && onglet && anomaly.datasetId && canWrite ? (
											<button
												type="button"
												disabled={busy === anomaly.id}
												onClick={() => void adopter(anomaly)}
												className="inline-flex items-center gap-1.5 rounded-lg bg-slate-800 px-3 py-1.5 text-xs font-semibold text-white hover:bg-slate-700 disabled:opacity-50"
											>
												<FiCheck className="h-3.5 w-3.5" />
												Adopter ce nom
											</button>
										) : (
											<span className="text-xs text-slate-400">Corriger la source</span>
										)}
									</td>
								</tr>
							);
						})}
					</tbody>
				</table>
			</div>
		</div>
	);
};

export default AnomalyBanner;
